import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Clock, DollarSign, Plus, Star, Users, ShieldAlert, CheckCircle2, AlertCircle, Inbox, UserCheck, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { api, fmtUSD } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { UserBadges } from '@/components/TrustBadge';
import { EmptyState } from '@/components/Primitives';
import { SecurityBanner } from '@/components/SecurityBanner';

const stateColor = (s) => ({
  paid_escrow: 'bg-[#EAF3FF] text-[#0E3F80]',
  completed_pending: 'bg-[#FFF6C7] text-[#7A5F00]',
  released: 'bg-[#DFF7EA] text-[#16764A]',
  disputed: 'bg-[#FFE1E4] text-[#A10B18]',
  refunded: 'bg-slate-100 text-slate-600',
}[s] || 'bg-slate-100 text-slate-600');

const stateLabel = (s) => ({
  paid_escrow: 'En séquestre',
  completed_pending: 'Valid. client',
  released: 'Libéré',
  disputed: 'Litige',
  refunded: 'Remboursé',
  accepted: 'Accepté',
  sent: 'Envoyé',
  rejected: 'Refusé',
}[s] || s);

export default function DashboardPage() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    refreshMe();
    const tasks = [
      api.get('/conversations').then((r) => setConversations(r.data.items || [])),
      api.get('/payments/mine').then((r) => setPayments(r.data.items || [])),
    ];
    if (user.role === 'jobeur') tasks.push(api.get('/services/mine').then((r) => setServices(r.data.items || [])));
    if (user.role === 'employeur') tasks.push(api.get('/requests/mine').then((r) => setRequests(r.data.items || [])));
    Promise.all(tasks).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [user?.id]);

  if (!user) return null;

  const totalEarnings = payments.filter((p) => p.status === 'released' && p.jobeur_id === user.id).reduce((s, p) => s + (p.breakdown?.jobeur_payout || 0), 0);
  const totalSpent = payments.filter((p) => p.client_id === user.id).reduce((s, p) => s + (p.breakdown?.client_total || 0), 0);
  const activeEscrow = payments.filter((p) => ['paid_escrow', 'completed_pending'].includes(p.status)).length;

  const verifLevel = (user.phone_verified ? 1 : 0) + (user.id_verified ? 1 : 0) + (user.trusted_jobeur ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Bonjour, {user.first_name}</h1>
          <p className="text-sm text-slate-600">Votre tableau de bord {user.role === 'jobeur' ? 'Jobeur' : 'Employeur'}</p>
        </div>
        <div className="flex items-center gap-2">
          {user.role === 'jobeur' && (
            <Button onClick={() => nav('/service/new')} className="bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="dash-create-service">
              <Plus size={16} className="mr-1" /> Publier un service
            </Button>
          )}
          {user.role === 'employeur' && (
            <Button onClick={() => nav('/request/new')} className="bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="dash-create-request">
              <Plus size={16} className="mr-1" /> Publier une demande
            </Button>
          )}
        </div>
      </div>

      {/* Verification tracker */}
      {user.role === 'jobeur' && !user.id_verified && (
        <Card className="p-4 bg-[#EAF3FF] border-[#C7E2FF]">
          <div className="flex items-start gap-3">
            <ShieldAlert className="text-[#0E3F80]" size={20} />
            <div className="flex-1">
              <p className="font-semibold text-[#0E3F80]">Vérifiez votre identité pour inspirer confiance</p>
              <p className="text-sm text-slate-700 mt-1">Les clients choisissent 3x plus les jobeurs vérifiés.</p>
              <Progress value={(verifLevel / 3) * 100} className="mt-2 h-2" />
              <Button size="sm" className="mt-3 bg-[#1677F2] hover:bg-[#0F5FCC]" onClick={() => nav('/verify-id')} data-testid="dash-verify-cta">
                Lancer la vérification
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={user.role === 'jobeur' ? Wallet : DollarSign} label={user.role === 'jobeur' ? 'Gains cumulés' : 'Dépenses'} value={fmtUSD(user.role === 'jobeur' ? totalEarnings : totalSpent)} accent="text-[#16764A]" bg="bg-[#DFF7EA]" />
        <StatCard icon={Briefcase} label={user.role === 'jobeur' ? 'Jobs réalisés' : 'Demandes'} value={user.role === 'jobeur' ? (user.jobs_completed || 0) : requests.length} />
        <StatCard icon={Clock} label="En cours" value={activeEscrow} accent="text-[#7A5F00]" bg="bg-[#FFF6C7]" />
        <StatCard icon={Star} label="Note moyenne" value={(user.rating_avg || 0).toFixed(1)} suffix={` (${user.rating_count || 0})`} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <UserBadges user={user} size="sm" />
      </div>

      <SecurityBanner variant="compact" />

      <Tabs defaultValue={user.role === 'jobeur' ? 'services' : 'requests'} className="">
        <TabsList>
          {user.role === 'jobeur' && <TabsTrigger value="services" data-testid="dash-tab-services">Mes services</TabsTrigger>}
          {user.role === 'employeur' && <TabsTrigger value="requests" data-testid="dash-tab-requests">Mes demandes</TabsTrigger>}
          <TabsTrigger value="jobs" data-testid="dash-tab-jobs">Jobs & paiements</TabsTrigger>
          <TabsTrigger value="messages" data-testid="dash-tab-messages">Messages</TabsTrigger>
        </TabsList>

        {user.role === 'jobeur' && (
          <TabsContent value="services" className="mt-4">
            {services.length === 0 ? (
              <EmptyState icon={Plus} title="Aucun service publié" description="Publiez votre premier service en 2 minutes." action={<Button onClick={() => nav('/service/new')} className="bg-[#1677F2] hover:bg-[#0F5FCC]">Publier un service</Button>} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {services.map((s) => (
                  <Card key={s.id} className="p-4" data-testid={`dash-service-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <Link to={`/service/${s.id}`} className="font-semibold hover:text-[#1677F2] truncate">{s.title}</Link>
                        <p className="text-xs text-slate-600">{s.category} · {s.jobeur_commune}</p>
                      </div>
                      <p className="font-mono-num font-bold text-[#1677F2]">{fmtUSD(s.price_cents)}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {user.role === 'employeur' && (
          <TabsContent value="requests" className="mt-4">
            {requests.length === 0 ? (
              <EmptyState icon={Plus} title="Aucune demande publiée" description="Publiez votre première demande et recevez des propositions." action={<Button onClick={() => nav('/request/new')} className="bg-[#1677F2] hover:bg-[#0F5FCC]">Publier une demande</Button>} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {requests.map((r) => (
                  <Card key={r.id} className="p-4" data-testid={`dash-request-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <Link to={`/request/${r.id}`} className="font-semibold hover:text-[#1677F2] truncate">{r.title}</Link>
                        <p className="text-xs text-slate-600">{r.category} · {r.commune}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'open' ? 'bg-[#EAF3FF] text-[#0E3F80]' : 'bg-slate-100 text-slate-600'}`}>{r.status}</span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value="jobs" className="mt-4">
          {payments.length === 0 ? (
            <EmptyState icon={Inbox} title="Aucun paiement" description="Vos jobs terminés apparaîtront ici." />
          ) : (
            <div className="space-y-2">
              {payments.map((p) => (
                <Card key={p.id} className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <Link to={`/quote/${p.quote_id}`} className="font-semibold text-sm hover:text-[#1677F2]">Quote #{p.quote_id.slice(0, 8)}</Link>
                      <p className="text-xs text-slate-600 mt-0.5">{p.payment_method} · {new Date(p.paid_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono-num font-bold">{fmtUSD(p.breakdown?.client_total)}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${stateColor(p.status)}`}>{stateLabel(p.status)}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages" className="mt-4">
          {conversations.length === 0 ? (
            <EmptyState title="Aucun message" description="Vos échanges apparaîtront ici." />
          ) : (
            <div className="space-y-2">
              {conversations.slice(0, 8).map((c) => {
                const other = c.participants?.find((p) => p.id !== user.id);
                return (
                  <Link key={c.id} to={`/messages/${c.id}`}>
                    <Card className="p-3 hover:border-[#C7E2FF]">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm">{other?.first_name} {other?.last_name}</p>
                        {c.unread > 0 && <span className="text-[10px] bg-[#1677F2] text-white px-2 py-0.5 rounded-full">{c.unread}</span>}
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5 truncate">{c.last_message?.text || 'Commencer…'}</p>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, suffix, accent = 'text-[#1677F2]', bg = 'bg-[#EAF3FF]' }) => (
  <Card className="p-4">
    <div className={`h-9 w-9 rounded-lg ${bg} ${accent} flex items-center justify-center`}>
      <Icon size={18} />
    </div>
    <p className="text-xs text-slate-600 mt-3">{label}</p>
    <p className="text-xl md:text-2xl font-bold font-mono-num text-[#0B1220]">{value}{suffix && <span className="text-xs text-slate-500 font-sans ml-1">{suffix}</span>}</p>
  </Card>
);
